/**
 * Unified JSON schema form group
 */
import {
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChange,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { get, isEmpty, isNil, isString } from 'lodash';
import { XuxInputComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';

import { WIDGET_COMPONENT } from '@shared/types';
import { WidgetListener } from '@containers/base/decorators';

import { xuxDynamicComponentsMap } from '../xux-dynamic-widget-mapping';
import { IFieldPreferences } from '../../form/services/schema-form/schema-form-interfaces';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-dynamic-widget',
  templateUrl: 'xux-dynamic-widget.component.html',
  styleUrls: ['xux-dynamic-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // Notice: It is not possible to just switch this component to OnPush without breaking a big part of the app
})
export class XuxDynamicWidgetComponent implements OnChanges, OnDestroy {
  @Input() public field: IFieldPreferences;
  @Input() public dynamicWidgetsMap: Record<string, Type<any>>;

  @ViewChild('widgetContainer', { static: true, read: ViewContainerRef })
  public widgetContainer: ViewContainerRef;

  protected factory?: ComponentFactory<object>;
  protected componentRef?: ComponentRef<any>;

  public constructor(
    private readonly componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  public ngOnChanges(
    changes: Record<keyof XuxDynamicWidgetComponent, SimpleChange | undefined>,
  ): void {
    this.instantiateWidget();

    /**
     * need to call ngOnChanges of a child component manually
     * with changes if we have needs to build some logic based on this life cycle hook
     * NOTE if you can avoid using ngOnChanges in your widget, please do it
     */
    this.componentRef.instance.ngOnChanges?.(changes);
  }

  public ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  public focus(): void {
    this.componentRef?.instance?.focus?.();
  }

  protected instantiateWidget(): void {
    if (this.componentRef) return; // already instantiated

    const widgetConstructor =
      isString(this.field.widget) || isNil(this.field.widget)
        ? this.getWidgetByNameFromField(this.field.widget)
        : this.field.widget;
    this.factory =
      this.componentFactoryResolver.resolveComponentFactory(widgetConstructor);
    this.widgetContainer.clear();
    this.componentRef = this.widgetContainer.createComponent(this.factory);
    this.componentRef.instance.field = this.field;

    if (
      this.field.eventBus$ &&
      WidgetListener.hasListeners(widgetConstructor)
    ) {
      const sub = this.field.eventBus$.subscribe((event) =>
        WidgetListener.dispatch(this.componentRef.instance, event),
      );
      this.componentRef.onDestroy(() => sub.unsubscribe());
    }
  }

  protected getWidgetByNameFromField(
    widgetName: WIDGET_COMPONENT | string,
  ): Type<any> {
    const dynamicComponentsMap = isEmpty(this.dynamicWidgetsMap)
      ? xuxDynamicComponentsMap
      : this.dynamicWidgetsMap;

    return get(dynamicComponentsMap, widgetName, XuxInputComponent);
  }
}
